import { getItem, type InventoryState } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import {
  bindingLabel,
  bindingOf,
  isAction,
  onBindingsChange,
  shortcutTitle,
} from '../settings/keybindings.js';
import { paintItemIcon } from './item-icon.js';
import { choosePotion, type PotionChoice } from './potion-choice.js';
import { uiFormat, uiText } from './text.js';

/**
 * The quick-potion slot: a health potion drunk without opening the bag, by
 * its key (A by default), a click or a tap. It shows the potion the next
 * drink takes (`choosePotion`) and how many of it are left, greyed when the
 * bag holds none.
 *
 * Intents only (CLAUDE.md rule 1): it sends the same `use_item` as the bag,
 * which the server checks and applies. The server keeps no potion cooldown,
 * so none is shown. At full health nothing is sent: the bag would let the
 * potion be drunk for nothing, a slip of the key should not.
 *
 * Two buttons, one state, as for the mount: one in the spell bar for a mouse,
 * one placed by the touch HUD under the thumb (each hidden on the other
 * device by the CSS).
 */
export interface QuickPotionOptions {
  onDrink: (itemId: string) => void;
  /** The key or the slot pressed at full health: a word in the chat. */
  onFull: () => void;
}

/** Shown greyed when the bag holds no potion, so the slot keeps its face. */
const EMPTY_FACE = 'potion-small';

export class QuickPotion {
  readonly barSlot: HTMLButtonElement;
  readonly touchSlot: HTMLButtonElement;
  private bag: InventoryState['bag'] = [];
  private hp = 0;
  private maxHp = 0;
  private icons: CssAtlas | null = null;
  private choice: PotionChoice | undefined;
  /** What the buttons last showed, so a frame that changes nothing touches nothing. */
  private shown = '';
  private readonly unbind: () => void;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || !isAction(event, 'potion')) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    event.preventDefault();
    this.drink();
  };

  constructor(private readonly options: QuickPotionOptions) {
    this.barSlot = this.makeSlot('spell-bar__slot quick-potion');
    this.touchSlot = this.makeSlot('quick-potion quick-potion--touch');
    window.addEventListener('keydown', this.onKeyDown);
    this.unbind = onBindingsChange(() => {
      this.shown = '';
      this.render();
    });
    this.render();
  }

  setInventory(inventory: InventoryState): void {
    this.bag = inventory.bag;
    this.render();
  }

  /** The player's health, every frame: the choice follows the wound. */
  setHealth(hp: number, maxHp: number): void {
    if (hp === this.hp && maxHp === this.maxHp) return;
    this.hp = hp;
    this.maxHp = maxHp;
    this.render();
  }

  setIcons(icons: CssAtlas | null): void {
    this.icons = icons;
    this.shown = '';
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.unbind();
    this.barSlot.remove();
    this.touchSlot.remove();
  }

  private drink(): void {
    const choice = this.choice;
    if (choice === undefined || this.hp <= 0) return;
    if (this.maxHp > 0 && this.hp >= this.maxHp) {
      this.options.onFull();
      return;
    }
    this.options.onDrink(choice.itemId);
  }

  private makeSlot(className: string): HTMLButtonElement {
    const slot = document.createElement('button');
    slot.type = 'button';
    slot.className = className;
    slot.addEventListener('click', () => {
      this.drink();
    });
    return slot;
  }

  private render(): void {
    this.choice = choosePotion(this.bag, Math.max(0, this.maxHp - this.hp));
    const choice = this.choice;
    const state = `${choice?.template.id ?? ''}:${String(choice?.count ?? 0)}`;
    if (state === this.shown) return;
    this.shown = state;
    const label =
      choice === undefined
        ? uiText('Aucune potion de vie dans ton sac')
        : uiFormat('Boire : {name} (×{count})', {
            name: uiText(choice.template.name),
            count: choice.count,
          });
    const title = uiText(shortcutTitle(label, 'potion'));
    for (const slot of [this.barSlot, this.touchSlot]) {
      slot.replaceChildren();
      slot.classList.toggle('quick-potion--none', choice === undefined);
      slot.title = title;
      slot.setAttribute('aria-label', title);
      const icon = document.createElement('span');
      icon.className = 'quick-potion__icon';
      paintItemIcon(icon, choice?.template ?? getItem(EMPTY_FACE), this.icons, '');
      const key = document.createElement('span');
      key.className = 'spell-bar__key';
      key.textContent = uiText(bindingLabel(bindingOf('potion')), 'key');
      slot.append(icon, key);
      if (choice !== undefined) {
        const count = document.createElement('span');
        count.className = 'quick-potion__count';
        count.textContent = String(choice.count);
        slot.append(count);
      }
    }
  }
}
